import React, { useMemo, useState } from "react";
import styles from "./OutstandingBalances.module.css";
import { OutstandingBalances as OutstandingBalancesData, OutstandingParty } from "../Models/Models";
import { AccountSection } from "./accountSection";

function formatMoney(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === "") return "₹0.00";
  const num = typeof value === "number" ? value : Number(value);
  if (Number.isNaN(num)) return "₹0.00";
  return `₹${num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatDate(value: unknown): string {
  if (value == null) return "--";
  const raw = String(value).trim();
  if (!raw) return "--";
  const ymd = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
  if (ymd) {
    const d = new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]));
    if (!Number.isNaN(d.getTime())) return d.toLocaleDateString("en-IN");
  }
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? "--" : d.toLocaleDateString("en-IN");
}

function matchesQuery(row: OutstandingParty, query: string): boolean {
  if (!query) return true;
  const haystack = [row.display_name, row.party_name, row.company_name, row.party_id, row.user_id]
    .map((v) => String(v ?? "").toLowerCase())
    .join(" ");
  return haystack.includes(query);
}

interface OutstandingBalancesProps {
  section: AccountSection.DEBTORS | AccountSection.CREDITORS;
  data: OutstandingBalancesData | null;
  loading: boolean;
  error: boolean;
  headerAction?: React.ReactNode;
  onOpenCustomer: (userId: number) => void;
  onOpenVendor: (vendorId: number) => void;
}

const OutstandingBalances: React.FC<OutstandingBalancesProps> = ({
  section,
  data,
  loading,
  error,
  headerAction,
  onOpenCustomer,
  onOpenVendor,
}) => {
  const [query, setQuery] = useState("");
  const isDebtor = section === AccountSection.DEBTORS;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const parties = isDebtor ? data?.debtors || [] : data?.creditors || [];
  const total = isDebtor ? data?.total_receivable : data?.total_payable;
  const count = isDebtor ? data?.debtor_count : data?.creditor_count;
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return parties.filter((row) => matchesQuery(row, q));
  }, [parties, query]);

  return (
    <div className={styles.panel}>
      <div className={styles.toolbar}>
        <div className={styles.toolbarRow}>
          <input
            className={styles.searchInput}
            placeholder={isDebtor ? "Search debtors..." : "Search creditors..."}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {headerAction}
        </div>
        <p className={styles.hint}>
          {isDebtor
            ? "Customers with an outstanding receivable. Debit is amount still billed; Credit is amount received or adjusted. Outstanding comes from posted ledger entries."
            : "Vendors with an outstanding payable. Balances come from posted ledger entries."}
          {query.trim() && parties.length > 0 ? ` Showing ${filtered.length} of ${parties.length}.` : ""}
        </p>
      </div>

      <div className={styles.statRow}>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>{isDebtor ? "Debtors" : "Creditors"}</div>
          <div className={styles.statValue}>{count ?? (loading ? "—" : 0)}</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>{isDebtor ? "Total receivable" : "Total payable"}</div>
          <div className={`${styles.statValue} ${isDebtor ? styles.receivable : styles.payable}`}>
            {loading && !data ? "—" : formatMoney(total)}
          </div>
        </div>
      </div>

      <div className={styles.tableWrap}>
        {loading && !data ? (
          <div className={styles.loading}>Loading...</div>
        ) : error ? (
          <div className={styles.error}>Failed to load outstanding accounts.</div>
        ) : filtered.length === 0 ? (
          <div className={styles.empty}>
            {parties.length === 0
              ? isDebtor
                ? "No customers currently have an outstanding receivable."
                : "No vendors currently have an outstanding payable."
              : "No matching accounts."}
          </div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.nameCell}>{isDebtor ? "Customer" : "Vendor"}</th>
                <th>Debit</th>
                <th>Credit</th>
                <th>Outstanding Balance</th>
                <th>Last transaction</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => (
                <tr key={`${row.party_type}-${row.party_id}-${row.user_id}`}>
                  <td className={styles.nameCell}>
                    <div className={styles.partyName}>{row.display_name || row.party_name || "-"}</div>
                  </td>
                  <td className={styles.debitCell}>{formatMoney(row.total_debit)}</td>
                  <td className={styles.creditCell}>{formatMoney(row.total_credit)}</td>
                  <td>
                    <span className={`${styles.outstanding} ${isDebtor ? "" : styles.payableBadge}`}>
                      {formatMoney(row.outstanding)}
                    </span>
                    <div className={styles.partyMeta}>
                      {isDebtor ? "Receivable" : "Payable"}
                    </div>
                  </td>
                  <td>{formatDate(row.last_transaction_date)}</td>
                  <td>
                    <button
                      type="button"
                      className={styles.viewBtn}
                      onClick={() =>
                        isDebtor ? onOpenCustomer(row.user_id) : onOpenVendor(row.party_id)
                      }
                    >
                      View Ledger
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default OutstandingBalances;
